/**
 * OverrideReasonModal - Capture structured rejection reasons
 * 
 * Phase 5E: Override Intelligence
 * Captures why advisors reject AI recommendations for learning.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { AlertTriangle, Loader2 } from 'lucide-react';

export type OverrideCategory = 
  | 'cost_concern'
  | 'customer_declined'
  | 'premature'
  | 'parts_unavailable'
  | 'tech_disagreed'
  | 'customer_deferred'
  | 'other';

export const OVERRIDE_OPTIONS: Array<{ value: OverrideCategory; label: string; description: string }> = [
  { value: 'cost_concern', label: 'Cost Concern', description: 'Customer concerned about price' },
  { value: 'customer_declined', label: 'Customer Declined', description: 'Customer explicitly said no' },
  { value: 'premature', label: 'Premature', description: 'Issue not urgent enough yet' },
  { value: 'parts_unavailable', label: 'Parts Unavailable', description: 'Cannot source required parts' },
  { value: 'tech_disagreed', label: 'Tech Disagreed', description: 'Technician disagrees with AI assessment' },
  { value: 'customer_deferred', label: 'Deferred', description: 'Customer wants to wait' },
  { value: 'other', label: 'Other', description: 'Different reason (specify below)' },
];

interface OverrideReasonModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (category: OverrideCategory, reason: string) => void;
  itemTitle?: string;
  isLoading?: boolean;
}

export function OverrideReasonModal({
  open,
  onOpenChange,
  onConfirm,
  itemTitle,
  isLoading = false,
}: OverrideReasonModalProps) {
  const [category, setCategory] = useState<OverrideCategory | ''>('');
  const [additionalNotes, setAdditionalNotes] = useState('');

  const handleConfirm = () => {
    if (!category) return;
    
    // Build reason text
    const selectedOption = OVERRIDE_OPTIONS.find(o => o.value === category);
    const reason = additionalNotes.trim() 
      ? `${selectedOption?.label}: ${additionalNotes.trim()}`
      : selectedOption?.label || category;
    
    onConfirm(category, reason);
    
    // Reset state
    setCategory('');
    setAdditionalNotes('');
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setCategory('');
      setAdditionalNotes('');
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
            Decline Recommendation
          </DialogTitle>
          <DialogDescription>
            {itemTitle 
              ? `Why are you declining "${itemTitle}"?`
              : 'Please select a reason for declining this recommendation.'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <RadioGroup value={category} onValueChange={(v) => setCategory(v as OverrideCategory)}>
            {OVERRIDE_OPTIONS.map((option) => (
              <div key={option.value} className="flex items-start space-x-3">
                <RadioGroupItem value={option.value} id={option.value} className="mt-1" />
                <Label htmlFor={option.value} className="flex flex-col cursor-pointer">
                  <span className="font-medium">{option.label}</span>
                  <span className="text-xs text-muted-foreground">{option.description}</span>
                </Label>
              </div>
            ))}
          </RadioGroup>

          <div className="space-y-2">
            <Label htmlFor="notes">Additional Notes (Optional)</Label>
            <Textarea
              id="notes"
              placeholder="Any additional context..."
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              className="h-20"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm} 
            disabled={!category || isLoading}
            variant="destructive"
          >
            {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Confirm Decline
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Inline content component for use in existing dialogs
 */
export interface OverrideReasonContentProps {
  rejectionReason: string;
  onReasonChange: (reason: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export function OverrideReasonContent({
  rejectionReason,
  onReasonChange,
  onConfirm,
  onCancel,
  isSubmitting,
}: OverrideReasonContentProps) {
  const [selectedCategory, setSelectedCategory] = useState<OverrideCategory | ''>('');
  const [notes, setNotes] = useState(rejectionReason);

  const handleConfirm = () => {
    const selectedOption = OVERRIDE_OPTIONS.find(o => o.value === selectedCategory);
    const fullReason = selectedCategory 
      ? `[${selectedOption?.label}] ${notes}`.trim()
      : notes;
    onReasonChange(fullReason);
    onConfirm();
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <Label>Why are you declining this recommendation?</Label>
        <RadioGroup value={selectedCategory} onValueChange={(v) => setSelectedCategory(v as OverrideCategory)}>
          {OVERRIDE_OPTIONS.map((option) => (
            <div key={option.value} className="flex items-start space-x-3">
              <RadioGroupItem value={option.value} id={`inline-${option.value}`} className="mt-0.5" />
              <Label htmlFor={`inline-${option.value}`} className="flex flex-col cursor-pointer">
                <span className="text-sm font-medium">{option.label}</span>
                <span className="text-xs text-muted-foreground">{option.description}</span>
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      <div className="space-y-2">
        <Label htmlFor="override-notes">Additional Notes (optional)</Label>
        <Textarea
          id="override-notes"
          placeholder="Any additional context..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
        />
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button 
          variant="destructive" 
          onClick={handleConfirm}
          disabled={!selectedCategory || isSubmitting}
        >
          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Reject
        </Button>
      </DialogFooter>
    </div>
  );
}
